/**
 * Missing Runtime Config Banner + Badge
 *
 * Makes an unset required runtime-config value obvious on the pack and
 * project feature pages: launches that include the feature fail until it is
 * set. Text only — the fix is the row's Runtime Config button, so there is
 * one path to the value.
 */

import { type ReactElement } from 'react';
import type { MissingRuntimeConfigItem } from '../../schemas/admin.schemas';

interface MissingRuntimeConfigBannerProps {
  items: MissingRuntimeConfigItem[];
  /** Pack page: how many projects inherit the failure. */
  linkedProjectCount?: number;
  /** Project page: name of the pack the project is linked to, if any. */
  packName?: string;
}

function whoFails(item: MissingRuntimeConfigItem, linkedProjectCount?: number): string {
  const users = item.gate_value ? `users with ${item.gate_value}` : 'all users';
  if (linkedProjectCount === undefined) return `Launches fail for ${users}.`;
  const projects = `${String(linkedProjectCount)} linked project${linkedProjectCount === 1 ? '' : 's'}`;
  return `Launches fail for ${users} on ${projects}.`;
}

export function MissingRuntimeConfigBanner({
  items, linkedProjectCount, packName,
}: MissingRuntimeConfigBannerProps): ReactElement | null {
  if (items.length === 0) return null;
  const count = items.length;
  return (
    <div className="missing-config-banner" role="alert">
      <strong className="missing-config-banner__title">
        {String(count)} feature{count === 1 ? '' : 's'} will block workspace launches
      </strong>
      <ul className="missing-config-banner__list">
        {items.map((item) => (
          <li key={item.feature_id}>
            <strong>{item.display_name}</strong>
            {item.source === 'pack' && packName ? ` (from pack ${packName})` : ''} needs{' '}
            {item.missing.map((name, i) => (
              <span key={name}>{i > 0 ? ', ' : ''}<code>{name}</code></span>
            ))}
            {' '}— not set at any level that applies here. {whoFails(item, linkedProjectCount)}
          </li>
        ))}
      </ul>
      <span className="missing-config-banner__hint">
        Set the value with the feature&apos;s Runtime Config button below.
      </span>
    </div>
  );
}

export function MissingRuntimeConfigBadge({
  item,
}: { item: MissingRuntimeConfigItem | undefined }): ReactElement | null {
  if (!item) return null;
  return (
    <span className="missing-config-badge" title="Required runtime config is not set; launches fail">
      needs {item.missing.join(', ')}
    </span>
  );
}
